import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShoppingBag, Upload, Users, Package, ArrowLeft, Image, DollarSign, Calendar, AlertCircle, Printer, Download, Filter, X, Edit, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import PrintOrder from '@/components/PrintOrder';
import EditUserModal from '@/components/EditUserModal';
import EditOrderModal from '@/components/EditOrderModal';
import SeasonsManager from '@/components/SeasonsManager';
import UserImport from '@/components/UserImport';
import AccountsManager from '@/components/AccountsManager';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPendingUsers, setLoadingPendingUsers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [uploadingPricat, setUploadingPricat] = useState(false);
  const [uploadingStock, setUploadingStock] = useState(false);
  const [rebuildingStock, setRebuildingStock] = useState(false);
  const [completingImages, setCompletingImages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageUrls, setImageUrls] = useState('');
  const [searchTermPrices, setSearchTermPrices] = useState('');
  const [editingProductPrices, setEditingProductPrices] = useState(null);
  const [pricesPT, setPricesPT] = useState('');
  const [pricesPVPR, setPricesPVPR] = useState('');
  const [savingPricing, setSavingPricing] = useState({}); // { [productId]: true }
  const [pricingDraft, setPricingDraft] = useState({}); // { [productId]: {pvpr, discount} }
  const [importingPedido, setImportingPedido] = useState(false);
  const [expandedSizesProductId, setExpandedSizesProductId] = useState(null);
  const [activeAdminTab, setActiveAdminTab] = useState('orders');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/stats/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar estadísticas');
    } finally {
      setStatsLoading(false);
    }
  };
  const [uploadingProgrammingPricat, setUploadingProgrammingPricat] = useState(false);
  const [programmingSeason, setProgrammingSeason] = useState('SS26');
  const [completingProgrammingImages, setCompletingProgrammingImages] = useState(false);
  const [printingOrder, setPrintingOrder] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    business_name: '',
    phone: ''
  });

  // Filter states
  const [filterClient, setFilterClient] = useState('');
  const [filterReference, setFilterReference] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  // User filters
  const [filterUserName, setFilterUserName] = useState('');
  const [filterContactName, setFilterContactName] = useState('');
  const [filterUserProvince, setFilterUserProvince] = useState('');
  const [showUserFilters, setShowUserFilters] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchUsers();
    fetchPendingUsers();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios pendientes');
    } finally {
      setLoadingPendingUsers(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario aprobado exitosamente');
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar usuario');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de rechazar este usuario? Será eliminado permanentemente.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario rechazado y eliminado');
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar usuario');
    }
  };

  const handleUploadPricat = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPricat(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-pricat`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`PRICAT cargado: ${response.data.products_created} productos, ${response.data.variants_created} variantes creadas`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar PRICAT');
    } finally {
      setUploadingPricat(false);
      e.target.value = '';
    }
  };

  const handleUploadStock = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingStock(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-stock`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message || 'Catálogo de stock actualizado');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar stock');
    } finally {
      setUploadingStock(false);
      e.target.value = '';
    }
  };

  const handleRebuildStock = async () => {
    if (!window.confirm('Se reconstruirá el catálogo de Stock con el último CSV de la carpeta FTP, completando imágenes, EANs y precios. ¿Continuar?')) return;
    setRebuildingStock(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/sync-ftp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Catálogo reconstruido desde FTP');
        fetchProducts();
      } else {
        toast.error(response.data.error || 'No se pudo reconstruir el catálogo');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reconstruir el catálogo desde FTP');
    } finally {
      setRebuildingStock(false);
    }
  };

  const handleUploadProgrammingPricat = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingProgrammingPricat(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('season', programmingSeason);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-programming-pricat`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`PRICAT ${response.data.season} procesado: ${response.data.products_created} productos creados, ${response.data.products_updated} actualizados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar PRICAT de programación');
    } finally {
      setUploadingProgrammingPricat(false);
      e.target.value = '';
    }
  };

  const handleCompleteProgrammingImages = async () => {
    if (!window.confirm(`¿Buscar imágenes automáticamente para ${programmingSeason} desde hi-tecupdatefile.weebly.com/ss26.html?\n\nEsto puede tardar varios minutos.`)) {
      return;
    }

    setCompletingProgrammingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/complete-programming-images?season=${programmingSeason}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ ${response.data.updated} productos actualizados con imágenes de ${response.data.checked} revisados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar imágenes');
    } finally {
      setCompletingProgrammingImages(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/admin/create-user`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario creado exitosamente');
      setNewUser({ email: '', password: '', business_name: '', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleEditImages = (product) => {
    setEditingProduct(product);
    setImageUrls(product.images.join('\n'));
  };

  const handleSaveImages = async () => {
    if (!editingProduct) return;

    try {
      const token = localStorage.getItem('token');
      const urls = imageUrls.split('\n').filter(url => url.trim() !== '');
      
      await axios.put(
        `${API}/admin/products/${editingProduct.id}/images`,
        urls,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Imágenes actualizadas exitosamente');
      setEditingProduct(null);
      setImageUrls('');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar imágenes');
    }
  };

  const handleEditPrices = (product) => {
    setEditingProductPrices(product);
    // Get prices from first variant (all have the same price)
    if (product.variants && product.variants.length > 0) {
      setPricesPT(product.variants[0].pt_price || '');
      setPricesPVPR(product.variants[0].pvpr_price || '');
    }
  };

  const handleSavePrices = async () => {
    if (!editingProductPrices) return;

    const pt = parseFloat(pricesPT);
    const pvpr = parseFloat(pricesPVPR);

    if (isNaN(pt) || isNaN(pvpr)) {
      toast.error('Por favor ingresa precios válidos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API}/admin/products/${editingProductPrices.id}/prices`,
        { pt_price: pt, pvpr_price: pvpr },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Precios actualizados exitosamente');
      setEditingProductPrices(null);
      setPricesPT('');
      setPricesPVPR('');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar precios');
    }
  };

  const getPricingDraft = (product) => {
    const draft = pricingDraft[product.id];
    if (draft) return draft;
    const v = (product.variants && product.variants[0]) || {};
    return {
      pvpr: (v.pvpr_price ?? 0).toFixed(2),
      discount: Math.round(((v.discount ?? 0.24)) * 10000) / 100,  // 24 (percent)
    };
  };

  const setDraftField = (productId, key, value) => {
    setPricingDraft(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [key]: value },
    }));
  };

  const savePricingRow = async (product) => {
    const draft = getPricingDraft(product);
    const pvpr = parseFloat((draft.pvpr || '').toString().replace(',', '.'));
    const discountPct = parseFloat((draft.discount || '').toString().replace(',', '.'));
    if (isNaN(pvpr) || pvpr < 0) {
      toast.error('PVP inválido');
      return;
    }
    if (isNaN(discountPct) || discountPct < 0 || discountPct >= 100) {
      toast.error('Descuento inválido (0-99)');
      return;
    }
    try {
      setSavingPricing(prev => ({ ...prev, [product.id]: true }));
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API}/admin/products/${product.id}/pricing`,
        { pvpr_price: pvpr, discount: discountPct / 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Precios actualizados · PT €${data.pt_price.toFixed(2)}`);
      setPricingDraft(prev => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingPricing(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const handleImportPedidoExcel = async (file) => {
    if (!file) return;
    try {
      setImportingPedido(true);
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/import-pedido-excel`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(data.message || `Añadidas ${data.inserted} referencias`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el Excel');
    } finally {
      setImportingPedido(false);
    }
  };

  const toggleVariantStock = async (product, variant, makeAvailable) => {
    try {
      const token = localStorage.getItem('token');
      const newStock = makeAvailable ? 10000 : 0;
      const { data } = await axios.patch(
        `${API}/admin/products/${product.id}/variants/${encodeURIComponent(variant.size)}/stock`,
        { stock: newStock },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        makeAvailable
          ? `Talla ${data.size} disponible`
          : `Talla ${data.size} marcada como no fabricada`
      );
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la talla');
    }
  };

  const filteredProducts = products.filter(p => {
    // Defensive check: ensure we have a valid string before calling toLowerCase()
    const modelName = (p.model_name || p.model || p.display_name || '').toString();
    const reference = (p.variants && p.variants[0] && p.variants[0].reference) ? p.variants[0].reference.toString() : '';
    const searchLower = searchTerm.toLowerCase();
    
    // Allow searching by name OR reference
    return modelName.toLowerCase().includes(searchLower) || reference.toLowerCase().includes(searchLower);
  });

  const filteredProductsPrices = products.filter(p => {
    const modelName = (p.model_name || p.model || p.display_name || '').toString();
    const reference = (p.variants && p.variants[0] && p.variants[0].reference) ? p.variants[0].reference.toString() : '';
    const searchLower = searchTermPrices.toLowerCase();
    
    // Allow searching by name OR reference
    return modelName.toLowerCase().includes(searchLower) || reference.toLowerCase().includes(searchLower);
  });

  const handleCompleteImages = async () => {
    if (!window.confirm('¿Buscar imágenes faltantes automáticamente desde hi-tecupdatefile.weebly.com?\n\nEsto puede tardar varios minutos.')) {
      return;
    }

    setCompletingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/complete-missing-images`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`✓ Completado: ${response.data.updated} productos actualizados con imágenes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar imágenes');
    } finally {
      setCompletingImages(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-green-100 text-green-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return texts[status] || status;
  };

  const handlePrint = (order) => {
    setPrintingOrder(order);
    setTimeout(() => {
      window.print();
      setPrintingOrder(null);
    }, 100);
  };

  // Get unique clients from orders
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(orders.map(o => o.business_name))];
    return clients.sort();
  }, [orders]);

  // Filtered orders with combined filters
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    // Filter by client
    if (filterClient) {
      filtered = filtered.filter(order => order.business_name === filterClient);
    }

    // Filter by date range
    if (filterDateFrom) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const fromDate = new Date(filterDateFrom);
        return orderDate >= fromDate;
      });
    }

    if (filterDateTo) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999); // Include full day
        return orderDate <= toDate;
      });
    }

    // Filter by reference
    if (filterReference) {
      filtered = filtered.filter(order => {
        return order.items.some(item => 
          item.reference?.toLowerCase().includes(filterReference.toLowerCase()) ||
          item.model_name?.toLowerCase().includes(filterReference.toLowerCase())
        );
      });
    }

    return filtered;
  }, [orders, filterClient, filterReference, filterDateFrom, filterDateTo]);

  // Clear all filters
  const clearFilters = () => {
    setFilterClient('');
    setFilterReference('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  // Get unique provinces from users
  const uniqueUserProvinces = useMemo(() => {
    const provinces = [...new Set(users.map(u => u.province).filter(Boolean))];
    return provinces.sort();
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filterUserName) {
      filtered = filtered.filter(user =>
        user.business_name?.toLowerCase().includes(filterUserName.toLowerCase())
      );
    }

    if (filterContactName) {
      filtered = filtered.filter(user =>
        user.contact_name?.toLowerCase().includes(filterContactName.toLowerCase())
      );
    }

    if (filterUserProvince) {
      filtered = filtered.filter(user => user.province === filterUserProvince);
    }

    return filtered;
  }, [users, filterUserName, filterContactName, filterUserProvince]);

  // Clear user filters
  const clearUserFilters = () => {
    setFilterUserName('');
    setFilterContactName('');
    setFilterUserProvince('');
  };

  // Export users to Excel
  const exportUsersToExcel = () => {
    if (filteredUsers.length === 0) {
      toast.error('No hay usuarios para exportar');
      return;
    }

    const excelData = filteredUsers.map(user => ({
      'Código Cliente': user.customer_code || '-',
      'Nombre del Negocio': user.business_name,
      'Nombre del Responsable': user.contact_name || '-',
      'Email': user.email,
      'Teléfono': user.phone || '-',
      'Provincia': user.province || '-',
      'Estado': user.approved ? 'Aprobado' : 'Pendiente',
      'Fecha de Registro': new Date(user.created_at).toLocaleDateString('es-ES')
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 15 }, // Código Cliente
      { wch: 30 }, // Nombre del Negocio
      { wch: 25 }, // Nombre del Responsable
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 20 }, // Provincia
      { wch: 12 }, // Estado
      { wch: 15 }  // Fecha de Registro
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');

    const filename = `Usuarios_HiTec_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`Archivo ${filename} descargado exitosamente`);
  };

  // Delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este pedido? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Pedido eliminado exitosamente');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar pedido');
    }
  };

  // Export to Excel with new format
  const exportToExcel = () => {
    if (filteredOrders.length === 0) {
      toast.error('No hay pedidos para exportar');
      return;
    }

    // Prepare data for Excel with new format
    const excelData = [];
    let totalPares = 0;
    let totalEuros = 0;
    
    filteredOrders.forEach(order => {
      order.items.forEach(item => {
        totalPares += item.quantity;
        totalEuros += item.price * item.quantity;
        
        excelData.push({
          'Nº Pedido': order.order_number,
          'Fecha': new Date(order.created_at).toLocaleDateString('es-ES'),
          'Cliente': order.business_name,
          'Email': order.user_email,
          'Referencia': item.reference,
          'MARCA': 'MACRON',
          'Producto': item.model_name,
          'Color': item.color,
          'Talla': item.size,
          'EAN': item.ean || '-',
          'Cantidad': item.quantity,
          'Precio Unitario': item.price.toFixed(2),
          'Subtotal': (item.price * item.quantity).toFixed(2),
          'Estado': getStatusText(order.status),
          'Observaciones': order.observations || '-',
          'Fecha Entrega': order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('es-ES') : '-'
        });
      });
    });

    // Add summary rows
    excelData.push({});
    excelData.push({
      'Nº Pedido': '',
      'Fecha': '',
      'Cliente': '',
      'Email': '',
      'Referencia': '',
      'MARCA': 'Total PARES',
      'Producto': '',
      'Color': '',
      'Talla': '',
      'EAN': '',
      'Cantidad': totalPares,
      'Precio Unitario': '',
      'Subtotal': '',
      'Estado': '',
      'Observaciones': '',
      'Fecha Entrega': ''
    });
    excelData.push({});
    excelData.push({
      'Nº Pedido': '',
      'Fecha': '',
      'Cliente': '',
      'Email': '',
      'Referencia': '',
      'MARCA': 'Total Pedido',
      'Producto': '',
      'Color': '',
      'Talla': '',
      'EAN': '',
      'Cantidad': totalPares,
      'Precio Unitario': '',
      'Subtotal': totalEuros.toFixed(2),
      'Estado': '',
      'Observaciones': '',
      'Fecha Entrega': ''
    });

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, // Nº Pedido
      { wch: 12 }, // Fecha
      { wch: 25 }, // Cliente
      { wch: 30 }, // Email
      { wch: 15 }, // Referencia
      { wch: 12 }, // MARCA
      { wch: 30 }, // Producto
      { wch: 20 }, // Color
      { wch: 8 },  // Talla
      { wch: 15 }, // EAN
      { wch: 10 }, // Cantidad
      { wch: 15 }, // Precio Unitario
      { wch: 12 }, // Subtotal
      { wch: 12 }, // Estado
      { wch: 30 }, // Observaciones
      { wch: 15 }  // Fecha Entrega
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');

    // Generate filename with current date
    const filename = `Pedidos_HiTec_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download
    XLSX.writeFile(wb, filename);
    toast.success(`Archivo ${filename} descargado exitosamente`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2">
              <ShoppingBag className="w-8 h-8 text-black" />
              <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-9" />
            </Link>
          </div>
        </div>
      </header>

      {/* Admin Dashboard */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Panel de Administración</h1>
          <p className="text-slate-600">Gestiona pedidos, usuarios y catálogo</p>
        </div>

        <Tabs value={activeAdminTab} onValueChange={(v) => {
          setActiveAdminTab(v);
          // Auto-cargar productos al abrir tabs que los necesitan
          if ((v === 'prices' || v === 'catalog' || v === 'images') && !products.length && !loadingProducts) {
            fetchProducts();
          }
          if (v === 'stats' && !stats && !statsLoading) {
            fetchStats();
          }
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-9 mb-8">
            <TabsTrigger data-testid="tab-orders" value="orders">
              <Package className="w-4 h-4 mr-2" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger data-testid="tab-stats" value="stats">
              <DollarSign className="w-4 h-4 mr-2" />
              Estadísticas
            </TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger data-testid="tab-accounts" value="accounts">
              <Building2 className="w-4 h-4 mr-2" />
              Cuentas
            </TabsTrigger>
            <TabsTrigger data-testid="tab-catalog" value="catalog">
              <Upload className="w-4 h-4 mr-2" />
              Catálogo
            </TabsTrigger>
            <TabsTrigger data-testid="tab-programming" value="programming">
              <Calendar className="w-4 h-4 mr-2" />
              Programación
            </TabsTrigger>
            <TabsTrigger data-testid="tab-seasons" value="seasons">
              <Calendar className="w-4 h-4 mr-2" />
              Temporadas
            </TabsTrigger>
            <TabsTrigger data-testid="tab-images" value="images">
              <Image className="w-4 h-4 mr-2" />
              Imágenes
            </TabsTrigger>
            <TabsTrigger data-testid="tab-prices" value="prices">
              <DollarSign className="w-4 h-4 mr-2" />
              Precios
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              {/* Header with filters toggle and export */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Todos los Pedidos</h2>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowFilters(!showFilters)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    Filtros
                    {(filterClient || filterReference || filterDateFrom || filterDateTo) && (
                      <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                        {[filterClient, filterReference, filterDateFrom, filterDateTo].filter(Boolean).length}
                      </span>
                    )}
                  </Button>
                  <Button
                    onClick={exportToExcel}
                    disabled={filteredOrders.length === 0}
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Exportar Excel
                  </Button>
                </div>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Client Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Cliente</Label>
                      <select
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="">Todos los clientes</option>
                        {uniqueClients.map((client, idx) => (
                          <option key={idx} value={client}>{client}</option>
                        ))}
                      </select>
                    </div>

                    {/* Reference Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Referencia/Producto</Label>
                      <Input
                        type="text"
                        placeholder="Buscar referencia..."
                        value={filterReference}
                        onChange={(e) => setFilterReference(e.target.value)}
                        className="w-full"
                      />
                    </div>

                    {/* Date From Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Fecha Desde</Label>
                      <Input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        className="w-full"
                      />
                    </div>

                    {/* Date To Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Fecha Hasta</Label>
                      <Input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">
                      Mostrando <strong>{filteredOrders.length}</strong> de <strong>{orders.length}</strong> pedidos
                    </p>
                    {(filterClient || filterReference || filterDateFrom || filterDateTo) && (
                      <Button
                        onClick={clearFilters}
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                      >
                        <X className="w-4 h-4" />
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {loadingOrders ? (
                <p className="text-slate-600">Cargando pedidos...</p>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 text-lg">
                    {orders.length === 0 ? 'No hay pedidos todavía' : 'No se encontraron pedidos con los filtros aplicados'}
                  </p>
                  {orders.length > 0 && (
                    <Button
                      onClick={clearFilters}
                      variant="outline"
                      className="mt-4"
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredOrders.map((order) => (
                    <div 
                      key={order.id} 
                      data-testid={`admin-order-${order.id}`}
                      className="border border-slate-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-slate-900">#{order.order_number}</h3>
                          <p className="text-sm text-slate-600">
                            {order.business_name} ({order.user_email})
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(order.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <Button
                            onClick={() => setEditingOrder(order)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                            Editar
                          </Button>
                          {user.role === 'admin' && (
                            <Button
                              onClick={() => handleDeleteOrder(order.id)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Eliminar
                            </Button>
                          )}
                          <Button
                            onClick={() => handlePrint(order)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Printer className="w-4 h-4" />
                            Imprimir
                          </Button>
                          <Button
                            data-testid={`admin-macron-csv-${order.id}`}
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await axios.get(`${API}/admin/orders/${order.id}/macron-po-csv`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob',
                                });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `PedidoCompra_Macron_${order.order_number}.csv`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                toast.success('CSV Macron descargado');
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Error al descargar CSV Macron');
                              }
                            }}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1 bg-black text-white hover:bg-neutral-800 border-black"
                          >
                            <Download className="w-4 h-4" />
                            CSV Macron
                          </Button>
                          <div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                            <p className="text-xl font-bold text-black mt-2">€{order.total.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-slate-700">
                              {item.model_name} ({item.color}, T{item.size}) x{item.quantity}
                            </span>
                            <span className="text-slate-900 font-semibold">
                              €{(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Nº pedido MACRON — se introduce cuando llega la confirmación del portal Macron */}
                      <div className="mt-3 bg-white border border-neutral-200 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <label className="text-xs uppercase tracking-widest text-neutral-500 font-bold whitespace-nowrap">
                          Nº pedido MACRON
                        </label>
                        <input
                          type="text"
                          data-testid={`macron-po-input-${order.id}`}
                          defaultValue={order.macron_po_number || ''}
                          placeholder="Introducir tras cargar CSV en B2B Macron"
                          className="flex-1 min-w-0 h-9 border border-neutral-300 rounded-md px-2 text-sm focus:outline-none focus:border-black"
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if ((order.macron_po_number || '') === val) return;
                            try {
                              const token = localStorage.getItem('token');
                              await axios.patch(
                                `${API}/admin/orders/${order.id}/macron-po`,
                                { macron_po_number: val || null },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              order.macron_po_number = val || null;
                              toast.success(val ? `Nº MACRON guardado: ${val}` : 'Nº MACRON borrado');
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Error guardando Nº MACRON');
                            }
                          }}
                        />
                        {order.macron_po_number && (
                          <span className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">
                            ✓ vinculado
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Stats Tab — Compras mensuales */}
          <TabsContent value="stats">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Estadísticas de compras</h2>
                  <p className="text-sm text-neutral-600">Ventas a Deportes Tomás y ahorro entregado.</p>
                </div>
                <Button onClick={fetchStats} variant="outline" className="border-black text-black hover:bg-black hover:text-white">
                  Refrescar
                </Button>
                <Button
                  data-testid="stats-export-excel"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.get(`${API}/admin/stats/purchases/excel`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob',
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                      a.download = `Estadisticas_Sudeporte_Macron_${today}.xlsx`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Excel descargado');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Error al descargar Excel');
                    }
                  }}
                  className="ml-2 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="w-4 h-4 mr-1" /> Excel
                </Button>
              </div>

              {statsLoading && <p className="text-slate-600 text-center py-8">Calculando…</p>}

              {stats && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
                    <StatCard label="Pedidos" value={stats.totals.orders} />
                    <StatCard label="Unidades" value={stats.totals.units} />
                    <StatCard label="Base" value={`€${stats.totals.base.toFixed(2)}`} />
                    <StatCard label="Total IVA" value={`€${stats.totals.with_iva.toFixed(2)}`} />
                    <StatCard label="Ahorro entregado" value={`€${stats.totals.savings.toFixed(2)}`} accent="emerald" />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="border border-neutral-200 rounded-xl overflow-hidden">
                      <div className="bg-neutral-100 px-4 py-2 text-xs uppercase tracking-widest font-black text-neutral-600">
                        Por mes (últimos 12)
                      </div>
                      <div className="divide-y divide-neutral-100">
                        {stats.by_month.length === 0 && (
                          <p className="text-sm text-neutral-500 px-4 py-6 text-center">Sin datos aún.</p>
                        )}
                        {stats.by_month.map((m) => (
                          <div key={m.month} className="grid grid-cols-5 gap-2 px-4 py-3 items-center text-sm">
                            <div className="font-bold">{m.month}</div>
                            <div>{m.orders} ped.</div>
                            <div>{m.units} und.</div>
                            <div>€{m.base.toFixed(2)}</div>
                            <div className="text-emerald-700 font-bold">−€{m.savings.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border border-neutral-200 rounded-xl overflow-hidden">
                      <div className="bg-neutral-100 px-4 py-2 text-xs uppercase tracking-widest font-black text-neutral-600">
                        Referencias más vendidas
                      </div>
                      <div className="divide-y divide-neutral-100">
                        {stats.by_reference.length === 0 && (
                          <p className="text-sm text-neutral-500 px-4 py-6 text-center">Sin ventas registradas.</p>
                        )}
                        {stats.by_reference.map((r, i) => (
                          <div key={i} className="grid grid-cols-3 gap-2 px-4 py-3 items-center text-sm">
                            <div className="font-bold col-span-2">
                              {r.model_name} <span className="text-neutral-500 font-normal">· {r.color}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-black">{r.units} und.</p>
                              <p className="text-[11px] text-neutral-500">€{r.base.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="border border-neutral-200 rounded-xl overflow-hidden mt-6">
                    <div className="bg-neutral-100 px-4 py-2 text-xs uppercase tracking-widest font-black text-neutral-600">
                      Por cliente
                    </div>
                    <div className="divide-y divide-neutral-100">
                      {stats.by_client.map((c, i) => (
                        <div key={i} className="grid grid-cols-4 gap-2 px-4 py-3 items-center text-sm">
                          <div className="font-bold col-span-2">{c.business_name || '—'}</div>
                          <div>{c.orders} ped. · {c.units} und.</div>
                          <div className="text-right">
                            <p className="font-black">€{c.base.toFixed(2)}</p>
                            <p className="text-[11px] text-emerald-700">ahorro −€{c.savings.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>


          {/* Users Tab */}
          <TabsContent value="users">
            {/* Pending Users Section */}
            {pendingUsers.length > 0 && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                  <span className="bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-lg">
                    {pendingUsers.length}
                  </span>
                  Usuarios Pendientes de Aprobación
                </h2>
                <p className="text-sm text-slate-700 mb-4">
                  Estos usuarios se han registrado y están esperando tu aprobación para acceder al catálogo completo.
                </p>
                {loadingPendingUsers ? (
                  <p className="text-slate-600">Cargando usuarios pendientes...</p>
                ) : (
                  <div className="space-y-3">
                    {pendingUsers.map((u) => (
                      <div 
                        key={u.id}
                        className="bg-white border border-yellow-300 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900">{u.business_name}</h3>
                            {u.contact_name && (
                              <p className="text-sm text-black font-medium">👤 {u.contact_name}</p>
                            )}
                            <p className="text-sm text-slate-600">{u.email}</p>
                            {u.phone && (
                              <p className="text-sm text-slate-600">📞 {u.phone}</p>
                            )}
                            {u.province && (
                              <p className="text-sm text-slate-600">📍 {u.province}</p>
                            )}
                            <p className="text-xs text-slate-500">
                              Registrado: {new Date(u.created_at).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <Button
                              onClick={() => handleApproveUser(u.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                            >
                              ✓ Aprobar
                            </Button>
                            <Button
                              onClick={() => handleRejectUser(u.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              size="sm"
                            >
                              ✗ Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* User Import Section */}
            <div className="mb-6">
              <UserImport onImportComplete={() => {
                fetchUsers();
                fetchPendingUsers();
              }} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Create User Form */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Crear Nuevo Usuario</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Los usuarios creados manualmente son aprobados automáticamente.
                </p>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <Label htmlFor="business_name">Nombre del Negocio</Label>
                    <Input
                      id="business_name"
                      data-testid="admin-create-business-input"
                      value={newUser.business_name}
                      onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="admin-create-email-input"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      data-testid="admin-create-phone-input"
                      type="tel"
                      value={newUser.phone}
                      onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="+34 600 000 000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Contraseña</Label>
                    <Input
                      id="password"
                      data-testid="admin-create-password-input"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                      className="mt-1"
                      minLength={6}
                    />
                  </div>
                  <Button 
                    data-testid="admin-create-user-btn"
                    type="submit" 
                    className="w-full bg-black hover:bg-neutral-800"
                  >
                    Crear Usuario
                  </Button>
                </form>
              </div>

              {/* Users List */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                {/* Header with filters toggle and export */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Todos los Usuarios</h2>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowUserFilters(!showUserFilters)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Filter className="w-4 h-4" />
                      Filtros
                      {(filterUserName || filterContactName || filterUserProvince) && (
                        <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          {[filterUserName, filterContactName, filterUserProvince].filter(Boolean).length}
                        </span>
                      )}
                    </Button>
                    <Button
                      onClick={exportUsersToExcel}
                      disabled={filteredUsers.length === 0}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Excel
                    </Button>
                  </div>
                </div>

                {/* Filters Panel */}
                {showUserFilters && (
                  <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 mb-4">
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-sm font-semibold mb-2">Nombre del Negocio</Label>
                        <Input
                          type="text"
                          placeholder="Buscar negocio..."
                          value={filterUserName}
                          onChange={(e) => setFilterUserName(e.target.value)}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold mb-2">Nombre del Responsable</Label>
                        <Input
                          type="text"
                          placeholder="Buscar responsable..."
                          value={filterContactName}
                          onChange={(e) => setFilterContactName(e.target.value)}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold mb-2">Provincia</Label>
                        <select
                          value={filterUserProvince}
                          onChange={(e) => setFilterUserProvince(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                        >
                          <option value="">Todas las provincias</option>
                          {uniqueUserProvinces.map((prov, idx) => (
                            <option key={idx} value={prov}>{prov}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                      <p className="text-sm text-slate-600">
                        Mostrando <strong>{filteredUsers.length}</strong> de <strong>{users.length}</strong> usuarios
                      </p>
                      {(filterUserName || filterContactName || filterUserProvince) && (
                        <Button
                          onClick={clearUserFilters}
                          variant="ghost"
                          size="sm"
                          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                        >
                          <X className="w-4 h-4" />
                          Limpiar filtros
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {loadingUsers ? (
                  <p className="text-slate-600">Cargando usuarios...</p>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">
                      {users.length === 0 ? 'No hay usuarios registrados' : 'No se encontraron usuarios con los filtros aplicados'}
                    </p>
                    {users.length > 0 && (
                      <Button
                        onClick={clearUserFilters}
                        variant="outline"
                        className="mt-4"
                        size="sm"
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {filteredUsers.map((u) => (
                      <div 
                        key={u.id} 
                        data-testid={`user-item-${u.id}`}
                        className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900">{u.business_name}</h3>
                            {u.contact_name && (
                              <p className="text-sm text-black font-medium">👤 {u.contact_name}</p>
                            )}
                            <p className="text-sm text-slate-600">{u.email}</p>
                            {u.phone && (
                              <p className="text-sm text-slate-600">📞 {u.phone}</p>
                            )}
                            {u.province && (
                              <p className="text-sm text-slate-600">📍 {u.province}</p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">
                              Registrado: {new Date(u.created_at).toLocaleDateString('es-ES')}
                            </p>
                            {u.approved ? (
                              <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                ✓ Aprobado
                              </span>
                            ) : (
                              <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                ⏳ Pendiente
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={() => setEditingUser(u)}
                            variant="outline"
                            size="sm"
                            className="ml-4"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Catalog Tab */}
          <TabsContent value="catalog">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload PRICAT */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Subir PRICAT</h2>
                <p className="text-slate-600 mb-6">
                  Archivo Excel con información de productos (CÓDIGO, MODELO, FOTOS)
                </p>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <label 
                    htmlFor="pricat-upload" 
                    className="cursor-pointer"
                  >
                    <Button 
                      data-testid="upload-pricat-btn"
                      type="button" 
                      disabled={uploadingPricat}
                      className="bg-black hover:bg-neutral-800"
                      onClick={() => document.getElementById('pricat-upload').click()}
                    >
                      {uploadingPricat ? 'Subiendo...' : 'Seleccionar PRICAT (.xlsx)'}
                    </Button>
                  </label>
                  <input
                    id="pricat-upload"
                    data-testid="pricat-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleUploadPricat}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Upload Stock */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Subir Stock</h2>
                <p className="text-slate-600 mb-6">
                  Archivo CSV con stock actualizado (SOLO actualiza stock, no precios)
                </p>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <label 
                    htmlFor="stock-upload" 
                    className="cursor-pointer"
                  >
                    <Button 
                      data-testid="upload-stock-btn"
                      type="button" 
                      disabled={uploadingStock}
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => document.getElementById('stock-upload').click()}
                    >
                      {uploadingStock ? 'Subiendo...' : 'Seleccionar Stock (.csv)'}
                    </Button>
                  </label>
                  <input
                    id="stock-upload"
                    data-testid="stock-file-input"
                    type="file"
                    accept=".csv"
                    onChange={handleUploadStock}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Rebuild Stock Catalog from FTP */}
            <div className="mt-6 bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">🔁 Reconstruir Catálogo de Stock (FTP)</h2>
              <p className="text-slate-600 mb-4">
                Descarga el último CSV de stock de la carpeta FTP y reconstruye el catálogo de Stock para que muestre
                EXACTAMENTE el stock real, completando automáticamente <strong>imágenes, EANs, tallas y precios</strong> desde
                los datos oficiales de hi-tecupdatefile.weebly.com (todas las temporadas).
              </p>
              <Button
                data-testid="rebuild-stock-btn"
                onClick={handleRebuildStock}
                disabled={rebuildingStock}
                className="bg-black hover:bg-neutral-800"
              >
                {rebuildingStock ? '🔄 Reconstruyendo...' : '🔁 Reconstruir desde FTP ahora'}
              </Button>
              {rebuildingStock && (
                <p className="text-sm text-slate-600 mt-3">⏳ Descargando FTP y reconstruyendo el catálogo. Espera unos segundos...</p>
              )}
              <p className="text-xs text-slate-500 mt-3">
                Esto también ocurre automáticamente cada día a las 8:30 y 20:30 (hora de Madrid).
              </p>
            </div>
            <div className="mt-6 bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">🔍 Completar Imágenes Automáticamente</h2>
              <p className="text-slate-600 mb-4">
                Busca automáticamente imágenes faltantes en hi-tecupdatefile.weebly.com y las añade a los productos.
              </p>
              <Button
                onClick={handleCompleteImages}
                disabled={completingImages}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {completingImages ? '🔄 Buscando imágenes...' : '✨ Completar Imágenes Faltantes'}
              </Button>
              {completingImages && (
                <p className="text-sm text-slate-600 mt-3">
                  ⏳ Esto puede tardar varios minutos. Por favor espera...
                </p>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-6 bg-neutral-100 border border-neutral-200 rounded-xl p-6">
              <h3 className="font-bold text-slate-900 mb-3">Instrucciones:</h3>
              <ul className="space-y-2 text-slate-700 text-sm">
                <li>1. Primero sube todos los archivos PRICAT para crear los productos base</li>
                <li>2. Luego sube el archivo de stock CSV para actualizar stock (solo stock, no precios)</li>
                <li>3. Usa "Completar Imágenes" para buscar automáticamente las imágenes que faltan</li>
                <li>3. Puedes actualizar el stock tantas veces como necesites</li>
                <li>4. Los cambios se reflejan inmediatamente en el catálogo</li>
              </ul>
            </div>
          </TabsContent>

          {/* Programming Tab */}
          <TabsContent value="programming">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-6 mb-6">
              <h2 className="text-3xl font-bold mb-2">Gestión de Programación</h2>
              <p className="text-blue-100">Administra productos de temporadas futuras (SS26, AW26, SS27, etc.)</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Upload Programming PRICAT */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Subir PRICAT Programación</h2>
                
                {/* Season Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Temporada
                  </label>
                  <select
                    value={programmingSeason}
                    onChange={(e) => setProgrammingSeason(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg"
                  >
                    <option value="SS26">SS26 - Primavera/Verano 2026</option>
                    <option value="AW26">AW26 - Otoño/Invierno 2026</option>
                    <option value="SS27">SS27 - Primavera/Verano 2027</option>
                    <option value="AW27">AW27 - Otoño/Invierno 2027</option>
                  </select>
                </div>

                <p className="text-slate-600 mb-4 text-sm">
                  Archivo Excel con productos de programación (sin stock físico actual)
                </p>

                <div className="border-2 border-dashed border-blue-300 rounded-xl p-8 text-center bg-blue-50">
                  <Calendar className="w-12 h-12 text-black mx-auto mb-4" />
                  <label htmlFor="programming-pricat-upload" className="cursor-pointer">
                    <Button 
                      type="button" 
                      disabled={uploadingProgrammingPricat}
                      className="bg-black hover:bg-neutral-800"
                      onClick={() => document.getElementById('programming-pricat-upload').click()}
                    >
                      {uploadingProgrammingPricat ? 'Subiendo...' : `Subir PRICAT ${programmingSeason}`}
                    </Button>
                  </label>
                  <input
                    id="programming-pricat-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleUploadProgrammingPricat}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Acceso Rápido</h2>
                <div className="space-y-3">
                  <a
                    href="/programming"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-neutral-100 hover:bg-blue-100 border border-neutral-200 rounded-lg p-4 transition"
                  >
                    <div className="flex items-center">
                      <Calendar className="w-6 h-6 text-black mr-3" />
                      <div>
                        <p className="font-semibold text-slate-900">Ver Catálogo Programación</p>
                        <p className="text-sm text-slate-600">Ver productos SS26 como cliente</p>
                      </div>
                    </div>
                  </a>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="text-2xl mr-3">ℹ️</div>
                      <div>
                        <p className="font-semibold text-green-900 mb-2">Fechas de Servicio Automáticas</p>
                        <ul className="text-sm text-green-800 space-y-1">
                          <li>• SS26 → 15 Febrero 2026</li>
                          <li>• AW26 → 15 Agosto 2026</li>
                          <li>• SS27 → 15 Febrero 2027</li>
                          <li>• AW27 → 15 Agosto 2027</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Complete Images for Programming */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">🔍 Completar Imágenes {programmingSeason}</h2>
              <p className="text-slate-600 mb-4">
                Busca automáticamente imágenes para productos de {programmingSeason} desde hi-tecupdatefile.weebly.com/ss26.html
              </p>
              <Button
                onClick={handleCompleteProgrammingImages}
                disabled={completingProgrammingImages}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {completingProgrammingImages ? '🔄 Buscando imágenes...' : `✨ Completar Imágenes ${programmingSeason}`}
              </Button>
              {completingProgrammingImages && (
                <p className="text-sm text-slate-600 mt-3">
                  ⏳ Esto puede tardar varios minutos. Por favor espera...
                </p>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-amber-600" />
                Importante sobre Programación
              </h3>
              <ul className="space-y-2 text-slate-700 text-sm">
                <li>✅ Los productos de programación NO tienen stock físico actual (stock = 0)</li>
                <li>✅ Se muestran en catálogo separado: /programming</li>
                <li>✅ La fecha de servicio se calcula automáticamente según la temporada</li>
                <li>✅ Los clientes pueden hacer pedidos anticipados si están aprobados</li>
                <li>⚠️ Puedes desactivar productos desde la pestaña "Imágenes" si se cancelan</li>
              </ul>
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Gestión de Imágenes de Productos</h2>
              
              <div className="bg-neutral-100 border border-neutral-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-700">
                  <strong>Nota:</strong> Aquí puedes editar manualmente las imágenes de cada producto. 
                  Los cambios se aplicarán inmediatamente. También puedes subir un nuevo PRICAT para actualizar automáticamente.
                </p>
              </div>

              {/* Search Bar */}
              <div className="mb-6">
                <Input
                  type="text"
                  placeholder="Buscar por nombre o referencia (ej: O090147001)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => !products.length && fetchProducts()}
                  className="w-full"
                />
              </div>

              {/* Load Products Button */}
              {!products.length && !loadingProducts && (
                <div className="text-center py-8">
                  <Button 
                    onClick={fetchProducts}
                    className="bg-black hover:bg-neutral-800"
                  >
                    Cargar Productos
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {loadingProducts && (
                <div className="text-center py-8">
                  <p className="text-slate-600">Cargando productos...</p>
                </div>
              )}

              {/* Products List */}
              {products.length > 0 && !editingProduct && (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <p className="text-slate-600 text-center py-8">
                      No se encontraron productos con ese nombre
                    </p>
                  ) : (
                    filteredProducts.map((product) => (
                      <div 
                        key={product.id}
                        className="border border-slate-200 rounded-lg p-4 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 mb-1">{product.model_name}</h3>
                            {product.variants && product.variants[0] && product.variants[0].reference && (
                              <p className="text-sm text-black font-semibold mb-1">
                                REF: {product.variants[0].reference}
                              </p>
                            )}
                            <p className="text-sm text-slate-600 mb-2">
                              {product.images.length > 0 
                                ? `${product.images.length} imagen(es)` 
                                : 'Sin imágenes'}
                            </p>
                            {product.images.length > 0 && (
                              <div className="flex space-x-2">
                                {product.images.slice(0, 3).map((img, idx) => (
                                  <img 
                                    key={idx}
                                    src={img} 
                                    alt={product.model_name}
                                    className="w-16 h-16 object-cover rounded border border-slate-200"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                ))}
                                {product.images.length > 3 && (
                                  <div className="w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
                                    <span className="text-xs text-slate-600">+{product.images.length - 3}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            onClick={() => handleEditImages(product)}
                            className="bg-black hover:bg-neutral-800 ml-4"
                            size="sm"
                          >
                            Editar Imágenes
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Edit Modal */}
              {editingProduct && (
                <div className="border-2 border-black rounded-lg p-6 bg-blue-50">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-slate-900">
                      Editando: {editingProduct.model_name}
                    </h3>
                    {editingProduct.variants && editingProduct.variants[0] && editingProduct.variants[0].reference && (
                      <p className="text-lg text-black font-semibold mt-1">
                        REF: {editingProduct.variants[0].reference}
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <Label htmlFor="image-urls" className="mb-2 block">
                      URLs de Imágenes (una por línea)
                    </Label>
                    <textarea
                      id="image-urls"
                      value={imageUrls}
                      onChange={(e) => setImageUrls(e.target.value)}
                      className="w-full h-48 p-3 border border-slate-300 rounded-lg font-mono text-sm"
                      placeholder="https://ejemplo.com/imagen1.jpg&#10;https://ejemplo.com/imagen2.jpg&#10;https://ejemplo.com/imagen3.jpg"
                    />
                    <p className="text-xs text-slate-600 mt-2">
                      Pega las URLs de las imágenes, una por línea. Puedes añadir hasta 7 imágenes.
                    </p>
                  </div>

                  {/* Preview */}
                  {imageUrls.trim() && (
                    <div className="mb-4">
                      <Label className="mb-2 block">Vista Previa:</Label>
                      <div className="flex flex-wrap gap-2">
                        {imageUrls.split('\n').filter(url => url.trim()).map((url, idx) => (
                          <img 
                            key={idx}
                            src={url.trim()} 
                            alt={`Preview ${idx + 1}`}
                            className="w-24 h-24 object-cover rounded border border-slate-300"
                            onError={(e) => {
                              e.target.style.border = '2px solid red';
                              e.target.alt = 'Error al cargar';
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <Button
                      onClick={handleSaveImages}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Guardar Cambios
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingProduct(null);
                        setImageUrls('');
                      }}
                      className="bg-slate-500 hover:bg-slate-600"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Prices Tab — MACRON Sudeporte pricing manager */}
          <TabsContent value="prices">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Precios & Referencias</h2>
                  <p className="text-sm text-neutral-600 mt-1">
                    Ajusta PVP Tarifa (Q) y descuento del cliente. El precio final PT se recalcula automáticamente.
                  </p>
                </div>

                {/* Excel import */}
                <div className="flex flex-col items-end">
                  <label
                    htmlFor="pedido-excel-input"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold cursor-pointer text-sm ${importingPedido ? 'bg-neutral-300 text-white cursor-not-allowed' : 'bg-black text-white hover:bg-neutral-800'}`}
                  >
                    <Upload className="w-4 h-4" />
                    {importingPedido ? 'Importando…' : 'Importar Excel pedido'}
                  </label>
                  <input
                    id="pedido-excel-input"
                    data-testid="import-pedido-excel-input"
                    type="file"
                    accept=".xlsx,.xls"
                    disabled={importingPedido}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleImportPedidoExcel(f);
                      e.target.value = '';
                    }}
                  />
                  <p className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                    Se añaden refs sin borrar las actuales
                  </p>
                </div>
              </div>

              {/* Search Bar */}
              <div className="mb-4">
                <Input
                  type="text"
                  placeholder="Buscar por nombre, artículo o referencia…"
                  value={searchTermPrices}
                  onChange={(e) => setSearchTermPrices(e.target.value)}
                  onFocus={() => !products.length && fetchProducts()}
                  className="w-full"
                />
              </div>

              {/* Auto-loaded on tab open */}
              {loadingProducts && (
                <div className="text-center py-8">
                  <p className="text-slate-600">Cargando productos…</p>
                </div>
              )}

              {products.length > 0 && (
                <div className="border border-neutral-200 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 bg-neutral-100 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-600 font-bold">
                    <div className="col-span-4">Referencia · Producto</div>
                    <div className="col-span-2 text-right">PVP Tarifa (Q) €</div>
                    <div className="col-span-2 text-right">Descuento %</div>
                    <div className="col-span-2 text-right">PT calculado €</div>
                    <div className="col-span-2 text-right">Acción</div>
                  </div>
                  <div className="divide-y divide-neutral-100 max-h-[600px] overflow-y-auto">
                    {filteredProductsPrices.length === 0 ? (
                      <p className="text-slate-600 text-center py-8">Sin resultados.</p>
                    ) : (
                      filteredProductsPrices.map((product) => {
                        const draft = getPricingDraft(product);
                        const pvprNum = parseFloat((draft.pvpr || '').toString().replace(',', '.')) || 0;
                        const discNum = parseFloat((draft.discount || '').toString().replace(',', '.')) || 0;
                        const ptPreview = (pvprNum * (1 - discNum / 100));
                        const ref = product.variants?.[0]?.reference || '';
                        const modelName = product.model_name || product.display_name || product.model;
                        const isSaving = !!savingPricing[product.id];
                        return (
                          <React.Fragment key={product.id}>
                          <div className="grid grid-cols-12 items-center px-4 py-3">
                            <div className="col-span-4 pr-2">
                              <p className="text-xs text-neutral-500 font-mono">{ref}</p>
                              <p className="text-sm font-bold text-black leading-tight">{modelName}</p>
                              <p className="text-[11px] text-neutral-500">
                                {product.color} · {(product.variants || []).length} tallas
                              </p>
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                data-testid={`pvpr-input-${product.id}`}
                                value={draft.pvpr}
                                onChange={(e) => setDraftField(product.id, 'pvpr', e.target.value)}
                                className="text-right h-9"
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                max="99"
                                data-testid={`discount-input-${product.id}`}
                                value={draft.discount}
                                onChange={(e) => setDraftField(product.id, 'discount', e.target.value)}
                                className="text-right h-9"
                              />
                            </div>
                            <div className="col-span-2 text-right">
                              <p className="text-lg font-black text-black leading-none">
                                €{ptPreview.toFixed(2)}
                              </p>
                              <p className="text-[10px] uppercase tracking-widest text-neutral-500">
                                IVA incl €{(ptPreview * 1.21).toFixed(2)}
                              </p>
                            </div>
                            <div className="col-span-2 text-right flex items-center justify-end gap-2">
                              <Button
                                data-testid={`toggle-sizes-${product.id}`}
                                onClick={() => setExpandedSizesProductId(expandedSizesProductId === product.id ? null : product.id)}
                                size="sm"
                                variant="outline"
                                className="border-neutral-300 text-neutral-700 hover:bg-neutral-100"
                              >
                                {expandedSizesProductId === product.id ? 'Ocultar' : 'Tallas'}
                              </Button>
                              <Button
                                data-testid={`save-pricing-${product.id}`}
                                onClick={() => savePricingRow(product)}
                                disabled={isSaving}
                                size="sm"
                                className="bg-black hover:bg-neutral-800 text-white"
                              >
                                {isSaving ? '…' : 'Guardar'}
                              </Button>
                            </div>
                          </div>
                          {expandedSizesProductId === product.id && (
                            <div className="col-span-12 bg-neutral-50 border-t border-neutral-200 px-4 py-3">
                              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                                Gestión de tallas — marca la disponibilidad de cada talla para Deportes Tomás
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {(product.variants || []).map((v) => {
                                  const available = v.stock > 0;
                                  return (
                                    <button
                                      type="button"
                                      key={`${product.id}-${v.size}`}
                                      data-testid={`toggle-variant-${product.id}-${v.size}`}
                                      onClick={() => toggleVariantStock(product, v, !available)}
                                      className={`px-3 py-2 rounded-lg border-2 font-black text-sm transition ${
                                        available
                                          ? 'border-black bg-black text-white'
                                          : 'border-neutral-300 bg-white text-neutral-400 line-through'
                                      }`}
                                      title={available ? 'Click para marcar como NO fabricada' : 'Click para marcar como disponible'}
                                    >
                                      {v.size}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-[11px] text-neutral-500 mt-2">
                                Las tallas tachadas están marcadas como <strong>no fabricadas por Macron</strong> y no aparecen para el cliente.
                              </p>
                            </div>
                          )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Seasons Tab */}
          <TabsContent value="seasons">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <SeasonsManager />
            </div>
          </TabsContent>

          {/* Accounts Tab */}
          <TabsContent value="accounts">
            <AccountsManager />
          </TabsContent>
        </Tabs>
      </main>

      {/* Print View - Hidden on screen, visible when printing */}
      {printingOrder && (
        <div className="hidden print:block">
          <PrintOrder order={printingOrder} />
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            fetchUsers();
            fetchPendingUsers();
          }}
        />
      )}

      {/* Edit Order Modal */}
      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSuccess={() => fetchOrders()}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className={`rounded-xl p-4 border ${accent === 'emerald' ? 'border-emerald-200 bg-emerald-50' : 'border-neutral-200 bg-neutral-50'}`}>
    <p className={`text-[10px] uppercase tracking-widest ${accent === 'emerald' ? 'text-emerald-700' : 'text-neutral-500'}`}>{label}</p>
    <p className={`text-2xl font-black mt-1 ${accent === 'emerald' ? 'text-emerald-700' : 'text-black'}`}>{value}</p>
  </div>
);

export default AdminDashboard;